import React from 'react'

import { Box, Link, Title } from '@factorialco/deprecated-design-system'
import { StatusVariant } from '@factorialco/f0-react'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Pencil } from '@factorialco/f0-react/icons/app'
import {
  ItalyLegalEntityTermsDocument,
  ItalyLegalEntityTermsQuery,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import {
  SignableOrganizationPerCountry,
  taxAgencyOrgsTexts,
} from 'modules/expenses/hooks/useTaxAgencyHomologations'
import TermsModal from 'modules/expenses/pages/settings/Content/LegalCompliance/TermsModal'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

type LegalEntity =
  ItalyLegalEntityTermsQuery['companies']['legalEntitiesConnection']['nodes'][number]

const termsStatusText = (termsAccepted?: boolean) => {
  if (termsAccepted) {
    return i18n.t(
      'expenses.settings.signed_invoices.italy.legal_entities.table.terms_status.accepted'
    )
  }
  return i18n.t('expenses.settings.signed_invoices.italy.legal_entities.table.terms_status.pending')
}
const activationStatusTag = (termsAccepted?: boolean): { color: StatusVariant; text: string } => {
  if (!termsAccepted) {
    return {
      color: 'critical',
      text: i18n.t(
        'expenses.settings.signed_invoices.italy.legal_entities.table.activation_status.not_activated'
      ),
    }
  } else
    return {
      color: 'positive',
      text: i18n.t(
        'expenses.settings.signed_invoices.italy.legal_entities.table.activation_status.activated'
      ),
    }
}

const ItalyLegalEntitiesPage = () => {
  const { company } = useCurrentSession()
  const [showTermsModal, setShowTermsModal] = React.useState(false)
  const organization = SignableOrganizationPerCountry.it
  const [selectedLegalEntity, setSelectedLegalEntity] = React.useState<string>()

  const dataSource = useGraphqlDataCollectionSource({
    document: ItalyLegalEntityTermsDocument,
    mapData: (data) => data.companies.legalEntitiesConnection.nodes,
    mapVariables: ({ sortings }) => ({
      companyId: company.id,
      sort: sortings?.[0],
    }),
    mapPagination: (data) => ({
      total: data.companies.legalEntitiesConnection.totalCount ?? 0,
    }),
    defaultSortings: {
      field: 'legalName',
      order: 'asc',
    },
    sortings: {
      legalName: {
        label: i18n.t('expenses.settings.signed_invoices.italy.legal_entities.table.legal_entity'),
      },
    },
    itemActions: (item: LegalEntity) => [
      {
        label: i18n.t(
          'expenses.settings.signed_invoices.italy.legal_entities.table.actions.activate'
        ),
        type: 'primary',
        icon: Pencil,
        onClick: () => {
          setSelectedLegalEntity(item.id)
          setShowTermsModal(true)
        },
        enabled: !item.signedDocumentsTerms[0]?.accepted,
      },
    ],
  })

  return (
    <ResponsivePage.Body>
      <Box gap="s32" paddingX="s24">
        <Box gap="s8">
          <Title
            variant="400"
            title={i18n.t('expenses.settings.signed_invoices.italy.legal_entities.title')}
            description={i18n.t(
              'expenses.settings.signed_invoices.italy.legal_entities.description'
            )}
            illustration="esignature"
          />
          <Link.Router external to={taxAgencyOrgsTexts(organization).termsLink}>
            {i18n.t('expenses.settings.signed_invoices.italy.legal_entities.help_link')}
          </Link.Router>
        </Box>
      </Box>
      <Box paddingX="s32">
        <OneDataCollection
          source={dataSource}
          visualizations={[
            {
              type: 'table',
              options: {
                columns: [
                  {
                    label: i18n.t(
                      'expenses.settings.signed_invoices.italy.legal_entities.table.legal_entity'
                    ),
                    render: (item: LegalEntity) => item.legalName,
                    sorting: 'legalName',
                  },
                  {
                    label: i18n.t(
                      'expenses.settings.signed_invoices.italy.legal_entities.table.terms_accepted'
                    ),
                    render: (item: LegalEntity) =>
                      termsStatusText(item.signedDocumentsTerms[0]?.accepted),
                  },
                  {
                    label: i18n.t(
                      'expenses.settings.signed_invoices.italy.legal_entities.table.status'
                    ),
                    render: (item: LegalEntity) => {
                      const statusTag = activationStatusTag(item.signedDocumentsTerms[0]?.accepted)
                      return {
                        type: 'status',
                        value: {
                          status: statusTag.color,
                          label: statusTag.text,
                        },
                      }
                    },
                  },
                  {
                    label: i18n.t(
                      'expenses.settings.signed_invoices.italy.legal_entities.table.activation_date'
                    ),
                    render: (item: LegalEntity) => {
                      const createdAt = item.signedDocumentsTerms[0]?.createdAt
                      return createdAt ? dateBackendDateTimeToUI(createdAt) : '-'
                    },
                  },
                ],
              },
            },
          ]}
        />
      </Box>
      {showTermsModal && (
        <TermsModal
          organization={organization}
          legalEntityId={selectedLegalEntity}
          termsLink={taxAgencyOrgsTexts(organization).termsLink}
          title={taxAgencyOrgsTexts(organization).termsModalTitle}
          description={taxAgencyOrgsTexts(organization).termsModalDescription}
          onClose={() => setShowTermsModal(false)}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default ItalyLegalEntitiesPage
